import React, { useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { Card, CardBody, Col, Row, Table } from "reactstrap";
import { FileTypeIcon, getFileKind } from "components/common/FileTypeIcon";
import { Link } from "react-router-dom";
import { DateTime, DateOnly } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import {
  EnquirySiteVisitStatus,
  EnquirySiteVisitStatusMap,
} from "features/EnquirySiteVisits/constants/enquiry-site-visit.constants";
import { useEnquirySiteVisitOverview } from "./EnquirySiteVisitOverviewContext";
import { historyMessage } from "utils/status-history";
import AssignmentsOverviewCard from "features/WorkAssignments/components/AssignmentsOverviewCard";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  shouldShowSiteVisitActionButtons,
  canReassignSiteVisit,
} from "features/EnquirySiteVisits/utils/siteVisitActionPolicy";
import { Timeline, type TimelineStep, type TimelineStepState } from "components/common/Timeline";
import type { EnquirySiteVisit } from "features/EnquirySiteVisits/types/enquiry-site-visit.types";

const API_URL = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

function parseAttachments(raw: unknown): string[] {
  if (Array.isArray(raw)) return (raw as string[]).filter(Boolean);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function resolveFileHref(file: string): string {
  const t = file.trim();
  if (!t) return "#";
  if (/^https?:\/\//i.test(t)) return t;
  const path = t.replace(/^\//, "");
  return API_URL ? `${API_URL}/${path}` : `/${path}`;
}

function displayFileName(file: string): string {
  try {
    if (/^https?:\/\//i.test(file)) {
      const last = new URL(file).pathname.split("/").filter(Boolean).pop();
      return last ? decodeURIComponent(last) : file;
    }
  } catch {
    /* ignore */
  }
  return decodeURIComponent(file.split("/").pop() ?? file);
}

const OverviewTab = () => {
  const { enquirySiteVisit, refetch } = useEnquirySiteVisitOverview();
  const { can } = useAccess();
  const canCreateAssignment = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canUpdateAssignment = can(Permissions.WORK_ASSIGNMENT.UPDATE);
  const completionNotes = historyMessage(
    enquirySiteVisit?.statusHistory,
    "COMPLETED",
  );
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const attachments = useMemo(
    () => parseAttachments(enquirySiteVisit?.attachments),
    [enquirySiteVisit?.attachments],
  );
  const imageAttachments = useMemo(
    () => attachments.filter((f) => getFileKind(f).isImage),
    [attachments],
  );

  return (
    <React.Fragment>
      <Row className="g-3">
        <Col xl={8} lg={7}>
          <Card>
            <CardBody>
              <div className="text-muted">
                <h6 className="mb-3 text-uppercase">Site visit info</h6>
                <div className="table-responsive">
                  <Table className="table-borderless mb-0">
                    <tbody>
                      {enquirySiteVisit?.code && (
                        <tr>
                          <th
                            className="ps-0"
                            scope="row"
                            style={{ width: "180px" }}
                          >
                            Code :
                          </th>
                          <td className="text-muted">
                            {enquirySiteVisit.code}
                          </td>
                        </tr>
                      )}

                      <tr>
                        <th className="ps-0" scope="row">
                          Scheduled :
                        </th>
                        <td className="text-muted">
                          {enquirySiteVisit?.scheduledAt ? (
                            <DateOnly date={enquirySiteVisit.scheduledAt} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Enquiry :
                        </th>
                        <td className="text-muted">
                          {enquirySiteVisit?.enquiryId != null &&
                          enquirySiteVisit.enquiryId !== "" &&
                          enquirySiteVisit.enquiry ? (
                            <Link
                              to={`/enquiry-management/enquiries/${enquirySiteVisit.enquiryId}`}
                              className="link fw-medium"
                            >
                              {enquirySiteVisit.enquiry.code}
                            </Link>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Address :
                        </th>
                        <td className="text-muted">
                          {enquirySiteVisit?.address || "—"}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          City :
                        </th>
                        <td className="text-muted">
                          {enquirySiteVisit?.city || "—"}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          State :
                        </th>
                        <td className="text-muted">
                          {enquirySiteVisit?.state || "—"}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Actual start :
                        </th>
                        <td className="text-muted">
                          {enquirySiteVisit?.actualStartDate ? (
                            <DateTime date={enquirySiteVisit.actualStartDate} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="ps-0" scope="row">
                          Completed :
                        </th>
                        <td className="text-muted">
                          {enquirySiteVisit?.completedAt ? (
                            <DateTime date={enquirySiteVisit.completedAt} />
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                      {enquirySiteVisit?.cancellationReason ? (
                        <tr>
                          <th className="ps-0" scope="row">
                            Cancellation reason :
                          </th>
                          <td className="text-muted">
                            {enquirySiteVisit.cancellationReason}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </Table>
                </div>

                <div className="pt-3 mt-4">
                  <h6 className="mb-3 text-uppercase">Remarks</h6>
                  <p
                    className="text-muted mb-0"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {enquirySiteVisit?.description || "—"}
                  </p>
                </div>

                {completionNotes ? (
                  <div className="pt-3 border-top border-top-dashed mt-4">
                    <h6 className="mb-3 text-uppercase">Completion Remarks</h6>
                    <p
                      className="text-muted mb-0"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {completionNotes}
                    </p>
                  </div>
                ) : null}

                <div className="pt-3 border-top border-top-dashed mt-4">
                  <Row className="gy-3">
                    {enquirySiteVisit?.created_at && (
                      <Col lg={3} sm={6}>
                        <div>
                          <p className="mb-2 text-uppercase fw-medium">
                            Created
                          </p>
                          <h5 className="fs-14 mb-0">
                            <DateTime date={enquirySiteVisit.created_at} />
                          </h5>
                        </div>
                      </Col>
                    )}
                    {enquirySiteVisit?.updated_at && (
                      <Col lg={3} sm={6}>
                        <div>
                          <p className="mb-2 text-uppercase fw-medium">
                            Last updated
                          </p>
                          <h5 className="fs-14 mb-0">
                            <DateTime date={enquirySiteVisit.updated_at} />
                          </h5>
                        </div>
                      </Col>
                    )}

                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Status</p>
                        <h5 className="fs-14 mb-0">
                          <StatusBadge
                            value={enquirySiteVisit?.status}
                            map={EnquirySiteVisitStatusMap}
                          />
                        </h5>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </CardBody>
          </Card>
          {attachments.length > 0 ? (
            <Card>
              <CardBody>
                <h6 className="mb-3 text-uppercase text-muted">Attachments</h6>
                <Row className="g-2 row-cols-2 row-cols-lg-5">
                  {attachments.map((url, idx) => {
                    const href = resolveFileHref(url);
                    const filename = displayFileName(url);
                    const { color, kindLabel, isImage } = getFileKind(url);
                    const BG: Record<string, string> = {
                      success: "bg-success-subtle",
                      danger: "bg-danger-subtle",
                      primary: "bg-primary-subtle",
                      warning: "bg-warning-subtle",
                      info: "bg-info-subtle",
                      secondary: "bg-secondary-subtle",
                    };
                    const imageIdx = isImage
                      ? imageAttachments.indexOf(url)
                      : -1;
                    return (
                      <Col key={idx}>
                        <div className="border rounded p-2 h-100 d-flex flex-column">
                          {isImage ? (
                            <button
                              type="button"
                              className="d-block rounded overflow-hidden bg-light mb-2 border-0 p-0 w-100"
                              style={{
                                aspectRatio: "4 / 3",
                                cursor: "zoom-in",
                              }}
                              onClick={() => {
                                setLightboxIndex(imageIdx);
                                setLightboxOpen(true);
                              }}
                            >
                              <img
                                src={href}
                                alt=""
                                className="w-100 h-100"
                                style={{ objectFit: "cover" }}
                              />
                            </button>
                          ) : (
                            <div
                              className={`rounded d-flex align-items-center justify-content-center mb-2 ${BG[color] ?? "bg-secondary-subtle"}`}
                              style={{
                                aspectRatio: "4 / 3",
                                minHeight: "64px",
                              }}
                            >
                              <FileTypeIcon
                                filename={url}
                                style={{ fontSize: "1.75rem" }}
                              />
                            </div>
                          )}
                          <span
                            className="badge bg-body-secondary text-muted border fw-normal mb-1 align-self-start text-uppercase"
                            style={{ fontSize: "0.6rem" }}
                          >
                            {kindLabel}
                          </span>
                          <p
                            className="small fw-medium text-body mb-2 text-break flex-grow-1"
                            title={filename}
                            style={{ fontSize: "0.75rem" }}
                          >
                            {filename}
                          </p>
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm w-100 py-1"
                            style={{ fontSize: "0.75rem" }}
                          >
                            <i className="ri-external-link-line align-middle me-1" />
                            Open
                          </a>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </CardBody>
            </Card>
          ) : null}
          {lightboxOpen ? (
            <Lightbox
              open
              close={() => setLightboxOpen(false)}
              index={lightboxIndex}
              slides={imageAttachments.map((f) => ({
                src: resolveFileHref(f),
              }))}
            />
          ) : null}
        </Col>
        {enquirySiteVisit?.id ? (
          <Col xl={4} lg={5}>
            <Card className="mb-3">
              <CardBody>
                <h6 className="mb-4 text-uppercase text-muted">Timeline</h6>
                <Timeline steps={buildSiteVisitTimeline(enquirySiteVisit)} />
              </CardBody>
            </Card>
            <AssignmentsOverviewCard
              sourceType={WorkAssignmentSourceType.SITE_VISIT}
              sourceId={enquirySiteVisit.id}
              sourceLabel={
                enquirySiteVisit.code
                  ? `Site visit ${enquirySiteVisit.code}`
                  : `Site visit #${enquirySiteVisit.id}`
              }
              canAssign={canCreateAssignment && shouldShowSiteVisitActionButtons(enquirySiteVisit.status)}
              canReassign={canUpdateAssignment && canReassignSiteVisit(enquirySiteVisit.status)}
              onSuccess={() => void refetch()}
            />
          </Col>
        ) : null}
      </Row>
    </React.Fragment>
  );
};

export default OverviewTab;

function buildSiteVisitTimeline(sv: EnquirySiteVisit): TimelineStep[] {
  const history = sv.statusHistory ?? [];
  return history.map((entry, i) => {
    const isLast = i === history.length - 1;
    const label =
      EnquirySiteVisitStatusMap[entry.status as EnquirySiteVisitStatus]
        ?.label ?? entry.status;
    const state: TimelineStepState = isLast
      ? entry.status === EnquirySiteVisitStatus.CANCELLED
        ? "skipped"
        : entry.status === EnquirySiteVisitStatus.COMPLETED
          ? "done"
          : "active"
      : "done";
    const byLabel = entry.user
      ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
      : entry.by != null ? `#${entry.by}` : undefined;
    return { label, timestamp: entry.at, by: byLabel, actor: entry.message ?? undefined, state };
  });
}
